import '@babel/runtime/regenerator';
import 'react-app-polyfill/ie9';
import React, { useState, useEffect } from 'react';
import ErrorBoundary from './errorBoundaries/errorBoundary';
import { getAuthToken, handleAuthTokenError, BASEURI, cookies } from './utils';
import { createRoot } from 'react-dom/client';
import { IntlProvider, useIntl } from 'react-intl';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import spanish from './lang/es.json';
import english from './lang/en.json';
import french from './lang/fr.json';
import arabic from './lang/ar.json';
import russian from './lang/ru.json';
import uzbek from './lang/uz.json';
import Highcharts from 'highcharts/highstock';

// Kendo react intl
import {
    IntlProvider as KendoReactIntlProvider,
    load,
    LocalizationProvider,
    loadMessages,
} from '@progress/kendo-react-intl';

import weekData from 'cldr-core/supplemental/weekData.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import numbers from 'cldr-numbers-full/main/es/numbers.json';
import dateFields from 'cldr-dates-full/main/es/dateFields.json';
import currencies from 'cldr-numbers-full/main/es/currencies.json';
import caGregorian from 'cldr-dates-full/main/es/ca-gregorian.json';
import timeZoneNames from 'cldr-dates-full/main/es/timeZoneNames.json';
import numbersFr from 'cldr-numbers-full/main/fr/numbers.json';
import dateFieldsFr from 'cldr-dates-full/main/fr/dateFields.json';
import currenciesFr from 'cldr-numbers-full/main/fr/currencies.json';
import caGregorianFr from 'cldr-dates-full/main/fr/ca-gregorian.json';
import timeZoneNamesFr from 'cldr-dates-full/main/fr/timeZoneNames.json';
import numbersAr from 'cldr-numbers-full/main/ar/numbers.json';
import dateFieldsAr from 'cldr-dates-full/main/ar/dateFields.json';
import currenciesAr from 'cldr-numbers-full/main/ar/currencies.json';
import caGregorianAr from 'cldr-dates-full/main/ar/ca-gregorian.json';
import timeZoneNamesAr from 'cldr-dates-full/main/ar/timeZoneNames.json';
import numbersRu from 'cldr-numbers-full/main/ru/numbers.json';
import dateFieldsRu from 'cldr-dates-full/main/ru/dateFields.json';
import currenciesRu from 'cldr-numbers-full/main/ru/currencies.json';
import caGregorianRu from 'cldr-dates-full/main/ru/ca-gregorian.json';
import timeZoneNamesRu from 'cldr-dates-full/main/ru/timeZoneNames.json';
import numbersUz from 'cldr-numbers-full/main/uz/numbers.json';
import dateFieldsUz from 'cldr-dates-full/main/uz/dateFields.json';
import currenciesUz from 'cldr-numbers-full/main/uz/currencies.json';
import caGregorianUz from 'cldr-dates-full/main/uz/ca-gregorian.json';
import timeZoneNamesUz from 'cldr-dates-full/main/uz/timeZoneNames.json';

load(
    likelySubtags,
    currencyData,
    weekData,
    numbers,
    currencies,
    caGregorian,
    dateFields,
    timeZoneNames,
    numbersFr,
    currenciesFr,
    caGregorianFr,
    dateFieldsFr,
    timeZoneNamesFr,
    numbersAr,
    currenciesAr,
    caGregorianAr,
    dateFieldsAr,
    timeZoneNamesAr,
    numbersRu,
    currenciesRu,
    caGregorianRu,
    dateFieldsRu,
    timeZoneNamesRu,
    numbersUz,
    currenciesUz,
    caGregorianUz,
    dateFieldsUz,
    timeZoneNamesUz
);
loadMessages(english, 'en');
loadMessages(spanish, 'es');
loadMessages(french, 'fr');
loadMessages(arabic, 'ar');
loadMessages(russian, 'ru');
loadMessages(uzbek, 'uz');

const highchartsLocales = {
  es: {
    loading: 'Cargando...',
    months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
    shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    exportButtonTitle: "Exportar",
    printButtonTitle: "Importar",
    rangeSelectorFrom: "Desde",
    rangeSelectorTo: "Hasta",
    rangeSelectorZoom: "Período",
    downloadPNG: 'Descargar imagen PNG',
    downloadJPEG: 'Descargar imagen JPEG',
    downloadPDF: 'Descargar documento PDF',
    downloadSVG: 'Descargar imagen vectorial SVG',
    resetZoom: "Restablecer zoom",
    resetZoomTitle: "Restablecer nivel de zoom 1:1",
    decimalPoint: ',',
    thousandsSep: '.',
    contextButtonTitle: "Menú contextual del gráfico"
  },
  en: {
    loading: 'Loading...',
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    exportButtonTitle: "Export",
    printButtonTitle: "Import",
    rangeSelectorFrom: "From",
    rangeSelectorTo: "To",
    rangeSelectorZoom: "Zoom",
    downloadPNG: 'Download PNG image',
    downloadJPEG: 'Download JPEG image',
    downloadPDF: 'Download PDF document',
    downloadSVG: 'Download SVG vector image',
    resetZoom: "Reset zoom",
    resetZoomTitle: "Reset zoom level 1:1",
    decimalPoint: '.',
    thousandsSep: ',',
    contextButtonTitle: "Chart context menu"
  },
  fr: {
    loading: 'Chargement...',
    months: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
    weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
    shortMonths: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
    exportButtonTitle: "Exporter",
    printButtonTitle: "Imprimer",
    rangeSelectorFrom: "De",
    rangeSelectorTo: "À",
    rangeSelectorZoom: "Zoom",
    downloadPNG: 'Télécharger image PNG',
    downloadJPEG: 'Télécharger image JPEG',
    downloadPDF: 'Télécharger document PDF',
    downloadSVG: 'Télécharger image vectorielle SVG',
    resetZoom: "Réinitialiser le zoom",
    resetZoomTitle: "Réinitialiser le niveau de zoom 1:1",
    decimalPoint: ',',
    thousandsSep: ' ',
    contextButtonTitle: "Menu contextuel du graphique"
  },
  ar: {
    loading: 'جارٍ التحميل...',
    months: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
    weekdays: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
    shortMonths: ['ينا', 'فبر', 'مار', 'أبر', 'ماي', 'يون', 'يول', 'أغس', 'سبت', 'أكت', 'نوف', 'ديس'],
    exportButtonTitle: "تصدير",
    printButtonTitle: "طباعة",
    rangeSelectorFrom: "من",
    rangeSelectorTo: "إلى",
    rangeSelectorZoom: "تكبير",
    downloadPNG: 'تحميل صورة PNG',
    downloadJPEG: 'تحميل صورة JPEG',
    downloadPDF: 'تحميل مستند PDF',
    downloadSVG: 'تحميل صورة SVG',
    resetZoom: "إعادة تعيين التكبير",
    resetZoomTitle: "إعادة تعيين مستوى التكبير 1:1",
    decimalPoint: '.',
    thousandsSep: ',',
    contextButtonTitle: "قائمة سياق الرسم البياني"
  },
  ru: {
    loading: 'Загрузка...',
    months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
    weekdays: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
    shortMonths: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
    exportButtonTitle: "Экспорт",
    printButtonTitle: "Печать",
    rangeSelectorFrom: "От",
    rangeSelectorTo: "До",
    rangeSelectorZoom: "Масштаб",
    downloadPNG: 'Скачать PNG изображение',
    downloadJPEG: 'Скачать JPEG изображение',
    downloadPDF: 'Скачать PDF документ',
    downloadSVG: 'Скачать SVG изображение',
    resetZoom: "Сбросить масштаб",
    resetZoomTitle: "Сбросить уровень масштаба 1:1",
    decimalPoint: ',',
    thousandsSep: ' ',
    contextButtonTitle: "Контекстное меню графика"
  },
  uz: {
    loading: 'Yuklanmoqda...',
    months: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun', 'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'],
    weekdays: ['Yakshanba', 'Dushanba', 'Seshanba', 'Chorshanba', 'Payshanba', 'Juma', 'Shanba'],
    shortMonths: ['Yan', 'Fev', 'Mar', 'Apr', 'May', 'Iyn', 'Iyl', 'Avg', 'Sen', 'Okt', 'Noy', 'Dek'],
    exportButtonTitle: "Eksport",
    printButtonTitle: "Chop etish",
    rangeSelectorFrom: "Dan",
    rangeSelectorTo: "Gacha",
    rangeSelectorZoom: "Masshtab",
    downloadPNG: 'PNG rasmni yuklab olish',
    downloadJPEG: 'JPEG rasmni yuklab olish',
    downloadPDF: 'PDF hujjatni yuklab olish',
    downloadSVG: 'SVG rasmni yuklab olish',
    resetZoom: "Masshtabni tiklash",
    resetZoomTitle: "Masshtab darajasini 1:1 ga tiklash",
    decimalPoint: ',',
    thousandsSep: ' ',
    contextButtonTitle: "Grafik kontekst menyusi"
  }
};


function Provide(props) {
    const [isLocaleLoaded, setIsLocaleLoaded] = useState(false);
    const [client, setClient] = useState({});
    const [messages, setMessages] = useState(english);
    const [locale, setLocale] = useState({
        "locale": "Español",
        "localeId": "es"
    });

    useEffect(() => {
        loadLocaleFromCookies();
        loadLocale();
        // Validate immediately on load
        validateAdminOrOrgToken();

        // Then validate every 5 minutes (300000 ms)
        const interval = setInterval(() => {
            validateAdminOrOrgToken();
        }, 120_000);

        return () => clearInterval(interval);
    }, [])

    useEffect(() => {
        const dir = locale.localeId === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.dir = dir;
        document.documentElement.lang = locale.localeId;
    }, [locale.localeId]);


    function validateAdminOrOrgToken() {
        if (localStorage.getItem('adminOrOrgToken')) {
            getAuthToken('adminOrOrgToken')
                .then(token => token)
                .catch(handleAuthTokenError)
                .then(token => {
                    if (!token) {
                        localStorage.removeItem('adminOrOrgToken');
                        localStorage.removeItem('token');
                        window.location.reload();
                        return false;
                    }
                    console.log('Valid adminOrOrgToken!')
                    return true
                });
        }
    }


    function addLocaleToCookies(lan) {
        const date = new Date();
        const daysToExpire = 30;
        date.setTime(date.getTime() + (daysToExpire * 24 * 60 * 60 * 1000));
        cookies.setItem('locale', lan, date);
    }

    function loadLocaleFromCookies() {
        const locale = cookies.getItem('locale');
        setLocaleData(locale);
    }

    function saveLocale(localeItem) {
        if (!client?.id) {
            return;
        }

        let url = BASEURI + `/api/dashboard/client/${client.id}/`,
            options = {
                method: 'put',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
                body: JSON.stringify({
                    lang: localeItem.localeId
                }),
            };
        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    // set the default locale to english
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => response.json())
                    .then((client) => {
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });

    }
    function loadLocale() {

        let url = BASEURI + '/api/dashboard/superuser/',
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
            };
        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {

                //no token exists or all tokens are expired
                if (token === false) {
                    return false;
                }

                options.headers.Authorization = `Bearer  ${token}`;

                fetch(url, options)
                    .then((response) => response.json())
                    .then((client_obj) => {
                        setClient(client_obj);
                        setLocaleData(client_obj.lang);
                        addLocaleToCookies(client_obj.lang);
                        setIsLocaleLoaded(true);
                    })
                    .catch(error => {
                        console.error('Error:', error.code);
                    });
            });
    }

    function setLocaleData(lang) {
        if (lang) {
            if (highchartsLocales[lang]) {
                Highcharts.setOptions({ lang: highchartsLocales[lang] });
            }
            if (lang === 'en') {
                setMessages(english);
                setLocale({
                    "locale": "English",
                    "localeId": "en"
                });

            } else if (lang === 'es') {
                setMessages(spanish);
                setLocale({
                    "locale": "Español",
                    "localeId": "es"
                });
            } else if (lang === 'fr') {
                setMessages(french);
                setLocale({
                    "locale": "Français",
                    "localeId": "fr"
                });
            } else if (lang === 'ar') {
                setMessages(arabic);
                setLocale({
                    "locale": "العربية",
                    "localeId": "ar"
                });
            } else if (lang === 'ru') {
                setMessages(russian);
                setLocale({
                    "locale": "Русский",
                    "localeId": "ru"
                });
            } else if (lang === 'uz') {
                setMessages(uzbek);
                setLocale({
                    "locale": "Oʻzbek",
                    "localeId": "uz"
                });
            }
        }
    }

    function handleLocaleChange(e) {
        const lan = e.value;

        if (highchartsLocales[lan.localeId]) {
            Highcharts.setOptions({ lang: highchartsLocales[lan.localeId] });
        }

        addLocaleToCookies(lan.localeId);
        saveLocale(lan);

        if (lan.localeId === 'es') {
            setLocale(lan);
            setMessages(spanish);
        } else if (lan.localeId === 'en') {
            setLocale(lan);
            setMessages(english);
        } else if (lan.localeId === 'fr') {
            setLocale(lan);
            setMessages(french);
        } else if (lan.localeId === 'ar') {
            setLocale(lan);
            setMessages(arabic);
        } else if (lan.localeId === 'ru') {
            setLocale(lan);
            setMessages(russian);
        } else if (lan.localeId === 'uz') {
            setLocale(lan);
            setMessages(uzbek);
        }
    }

    const isRTL = locale.localeId === 'ar';

    return (
        <div dir={isRTL ? 'rtl' : 'ltr'}>
            <LocalizationProvider language={locale.localeId} dir={isRTL ? 'rtl' : 'ltr'}>
                <KendoReactIntlProvider locale={locale.localeId}>
                    <IntlProvider locale={locale.localeId} messages={messages}>
                        <ReactIntl
                            handleLocaleChange={handleLocaleChange}
                            locale={locale} />
                    </IntlProvider>
                </KendoReactIntlProvider>
            </LocalizationProvider>
        </div>)
}

function ReactIntl(props) {
    const intl = useIntl();

    return (<ErrorBoundary preserveChildrenOnAuthExpired>
        <App
            handleLocaleChange={props.handleLocaleChange}
            locale={props.locale}
            intl={intl} />
    </ErrorBoundary>)
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<Provide />);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
